// 万屋（里から開く1画面 #/yorozuya。看板娘はあかね）。品物（材木・ガチャ券・錬成札）は買うたびに値上がりし、日付が変わると戻る。道具は一度買えばずっと使える解放。
// 表示は Profile から core の関数で計算し、買ったら /api/me を取り直して描き直す。
import { BATTLE_CONFIG, ITEM_UNLOCK, shopGoods, shopItems, type ShopGoodId } from '@hyakki/core';
import { sfx } from '../audio';
import { api } from '../api';
import { battleItemArt } from '../assets';
import { act, state } from '../state';
import { esc, fmt, jstDay } from './dom';
import { icon } from './icons';
import { toast } from './overlay';
import { go } from '../router';
import { AKANE, daypart, pick } from './shoplines';
import { mountShopScene } from './shopscene';
import { errorText } from './status';

const GOOD_ICON: Record<ShopGoodId, string> = { zaimoku: 'ri-zaimoku', kuji: 'ri-kuji', fuse: 'ri-ticket' };

type Tab = 'goods' | 'items';

/** あかねの一言・所持・下の枠（品物／道具の切り替え） */
function view(tab: Tab): { canBuy: boolean; wallet: string; panel: string } {
  const p = state.profile;
  if (!p) return { canBuy: false, wallet: '', panel: '' };
  const zeni = state.resources.zeni ?? 0;
  const goods = shopGoods(p, jstDay());
  const items = shopItems(p).filter((x) => x.id !== 'heal');
  const canBuy = goods.some((g) => !g.soldOut && zeni >= g.price) || items.some((x) => !x.owned && x.available && zeni >= x.price);
  const tabs = `<div class="tabs seg-tabs yz-tabs" role="tablist">${(
    [
      ['goods', '品物'],
      ['items', '道具'],
    ] as const
  )
    .map(([id, label]) => `<button class="tab-btn ${tab === id ? 'on' : ''}" role="tab" aria-selected="${tab === id}" data-tab="${id}">${label}</button>`)
    .join('')}</div>`;
  const list =
    tab === 'goods'
      ? `<ul class="yz-goods">${goods
          .map(
            (g) => `<li class="yz-good">
        <i class="ri ${GOOD_ICON[g.id]} yz-gi"></i>
        <span class="yz-gn"><b>${esc(g.name)}</b><small>×${fmt(g.amount)}</small></span>
        <span class="yz-gc">${g.daily ? `${g.bought}/${g.daily}` : g.bought ? `今日${g.bought}回` : ''}</span>
        <button class="btn primary yz-buy" data-good="${g.id}" ${g.soldOut || zeni < g.price ? 'disabled' : ''}>${g.soldOut ? '売り切れ' : `<i class="ri ri-zeni"></i>${fmt(g.price)}`}</button>
      </li>`,
          )
          .join('')}</ul>`
      : `<ul class="yz-items">${items
          .map((x) => {
            const d = BATTLE_CONFIG.items[x.id];
            const btn = x.owned
              ? `<span class="yz-owned">${icon('check')}解放済み</span>`
              : x.available
                ? `<button class="btn primary yz-buy" data-item="${x.id}" ${zeni < x.price ? 'disabled' : ''}><i class="ri ri-zeni"></i>${fmt(x.price)}</button>`
                : `<span class="yz-lock">${icon('lock')}主Lv${ITEM_UNLOCK[x.id]}</span>`;
            return `<li class="yz-item ${x.owned ? 'owned' : ''} ${x.available || x.owned ? '' : 'locked'}" title="${esc(d.desc)}">
          <span class="it-art yz-art" style="${battleItemArt(x.id)}"></span>
          <span class="yz-in"><b>${esc(d.name)}</b><small>${esc(d.desc)}</small></span>${btn}</li>`;
          })
          .join('')}</ul>`;
  return { canBuy, wallet: `<span><i class="ri ri-zeni"></i><b>${fmt(zeni)}</b></span>`, panel: tabs + list };
}

/** 里の見出しの「万屋」から */
export const openYorozuya = () => go('yorozuya');

/** 万屋の1画面（screens/shops.ts の mount） */
export function mountYorozuya(el: HTMLElement) {
  let tab: Tab = 'goods';
  // 押すたびに「品物・道具の案内（銭が足りなければ またどうぞ）」と雑談を交互に
  let pokes = 0;
  const sc = mountShopScene(el, 'yorozuya', '万屋', { src: '/art/shopgirls/akane.webp', name: 'あかね' }, () =>
    sc.say(pokes++ % 2 ? pick(AKANE.poke) : !view(tab).canBuy ? pick(AKANE.poor) : pick(AKANE[tab])),
  );
  const body = sc.panel;
  body.classList.add('yz');
  const redraw = () => {
    const v = view(tab);
    sc.wallet(v.wallet);
    body.innerHTML = v.panel;
  };
  redraw();
  sc.say(pick(AKANE.greet[daypart()]));
  body.addEventListener('click', async (e) => {
    const t = (e.target as HTMLElement).closest<HTMLElement>('[data-tab]');
    if (t) {
      if (tab === t.dataset.tab) return;
      tab = t.dataset.tab as Tab;
      redraw();
      return sc.say(pick(AKANE[tab]));
    }
    const b = (e.target as HTMLElement).closest<HTMLButtonElement>('.yz-buy');
    if (!b || b.disabled) return;
    body.querySelectorAll<HTMLButtonElement>('.yz-buy').forEach((x) => (x.disabled = true));
    try {
      const target = b.dataset.good ? { good: b.dataset.good } : { item: b.dataset.item! };
      await act(api.buyYorozuya(target));
      sfx('coin');
      if (b.dataset.item) {
        const name = BATTLE_CONFIG.items[b.dataset.item as keyof typeof BATTLE_CONFIG.items].name;
        toast(`${name}を解放しました`, 'good');
        sc.say(pick(AKANE.unlocked, { name }));
      } else sc.say(pick(AKANE.bought));
    } catch (err) {
      toast(errorText(err), 'error');
    }
    redraw();
  });
}
